import { useEffect, useId, useRef, useState, type DragEvent } from 'react';
import { useMediaQuery } from '../../../shared/layout.js';
import { tone } from '../../../shared/components/tokens.js';
import { buttonClasses } from '../../../ui/Button.js';
import { CameraIcon } from '../../../ui/icons.js';
import type { UploadState } from './useUploads.js';
import { validatePhotos, type PhotoLimits, type Rejection } from './validate.js';

/** Téléphone ou tablette : pointeur grossier (doigt). Sans `matchMedia`, mode ordinateur. */
export const TOUCH_QUERY = '(pointer: coarse)';

const STATE_LABELS: Record<UploadState['state'], string> = {
  waiting: 'En attente',
  uploading: 'Envoi…',
  done: 'Envoyée',
  error: 'Erreur',
};

/**
 * Choix de photos, composant unique (vague 4) : photos du dossier, complément, messages du garage,
 * question du client au portail.
 * - Ordinateur : glisser-déposer et « Choisir sur l'ordinateur ».
 * - Téléphone : « Prendre une photo » (appareil arrière) et « Choisir dans la galerie ».
 * Sélection multiple, aperçu local (URL d'objet, jamais envoyée ailleurs), retrait avant envoi,
 * état de chaque envoi, erreurs claires (format, taille, nombre ; HEIC : message de l'API).
 */
export function PhotoPicker({
  files,
  onChange,
  states,
  limits = {},
  disabled = false,
  label = 'Photos',
}: {
  files: File[];
  onChange: (files: File[]) => void;
  /** État d'envoi de chaque fichier (`useUploads`). */
  states?: Map<File, UploadState>;
  limits?: PhotoLimits;
  disabled?: boolean;
  /** Nom du groupe pour les lecteurs d'écran. */
  label?: string;
}) {
  const touch = useMediaQuery(TOUCH_QUERY);
  const ids = { computer: useId(), camera: useId(), gallery: useId() };
  const [rejected, setRejected] = useState<Rejection[]>([]);
  const [dragging, setDragging] = useState(false);

  const add = (picked: File[]) => {
    if (disabled || picked.length === 0) return;
    const { accepted, rejected: refused } = validatePhotos(picked, files.length, limits);
    setRejected(refused);
    if (accepted.length) onChange([...files, ...accepted]);
  };

  const input = (id: string, extra: { capture?: 'environment'; multiple: boolean }) => (
    <input
      id={id}
      type="file"
      accept="image/*"
      className="sr-only"
      disabled={disabled}
      multiple={extra.multiple}
      {...(extra.capture ? { capture: extra.capture } : {})}
      onChange={(e) => {
        add(Array.from(e.target.files ?? []));
        e.target.value = '';
      }}
    />
  );
  const buttonCls = buttonClasses({ variant: 'secondary', block: true, className: 'cursor-pointer' });

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    add(Array.from(e.dataTransfer?.files ?? []));
  };

  const done = states ? files.filter((f) => states.get(f)?.state === 'done').length : 0;
  const sending = states ? files.some((f) => states.get(f)?.state === 'uploading' || states.get(f)?.state === 'waiting') : false;
  const room = limits.max !== undefined ? Math.max(0, limits.max - files.length) : undefined;

  return (
    <div role="group" aria-label={label} className="space-y-3">
      {touch ? (
        <div className="grid grid-cols-2 gap-2">
          <label htmlFor={ids.camera} className={buttonCls} aria-disabled={disabled || undefined}>
            <CameraIcon /> Prendre une photo
          </label>
          {input(ids.camera, { capture: 'environment', multiple: false })}
          <label htmlFor={ids.gallery} className={buttonCls} aria-disabled={disabled || undefined}>
            Choisir dans la galerie
          </label>
          {input(ids.gallery, { multiple: true })}
        </div>
      ) : (
        <div
          data-testid="photo-drop-zone"
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-4 py-5 text-center ${
            dragging ? tone.dropActive : `${tone.border} ${tone.surface}`
          }`}
        >
          <p className={`text-sm ${tone.textMuted}`}>Glissez vos photos ici, ou</p>
          <label htmlFor={ids.computer} className={buttonClasses({ variant: 'secondary', className: 'cursor-pointer' })} aria-disabled={disabled || undefined}>
            <CameraIcon /> Choisir sur l’ordinateur
          </label>
          {input(ids.computer, { multiple: true })}
        </div>
      )}

      {room !== undefined && files.length > 0 ? (
        <p className={`text-sm ${tone.textMuted}`}>
          {files.length} photo{files.length > 1 ? 's' : ''} choisie{files.length > 1 ? 's' : ''}
          {room > 0 ? ` · encore ${room} possible${room > 1 ? 's' : ''}` : ' · maximum atteint'}
        </p>
      ) : null}

      {rejected.length > 0 ? (
        <ul role="alert" className={`space-y-1 rounded-xl border p-3 text-sm ${tone.dangerBox}`}>
          {rejected.map((r, i) => (
            <li key={`${r.name}-${i}`}>{r.message}</li>
          ))}
        </ul>
      ) : null}

      {states && files.length > 0 && (sending || done > 0) ? (
        <div>
          <p className={`mb-1 text-sm ${tone.textMuted}`}>
            {done}/{files.length} photo{files.length > 1 ? 's' : ''} envoyée{done > 1 ? 's' : ''}
          </p>
          <div
            role="progressbar"
            aria-label="Envoi des photos"
            aria-valuemin={0}
            aria-valuemax={files.length}
            aria-valuenow={done}
            className={`h-2 overflow-hidden rounded-full ${tone.surfaceMuted}`}
          >
            <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${(done / files.length) * 100}%` }} />
          </div>
        </div>
      ) : null}

      {files.length > 0 ? (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {files.map((f, i) => {
            const st = states?.get(f);
            const locked = st?.state === 'uploading' || st?.state === 'done' || disabled;
            return (
              <li key={`${f.name}-${f.size}-${i}`} className="relative">
                <Preview file={f} alt={`Photo ${i + 1}`} />
                {st ? (
                  <span
                    className={`absolute bottom-1 left-1 rounded px-1.5 text-2xs font-medium ${tone.overlay}`}
                    data-testid="upload-state"
                  >
                    {STATE_LABELS[st.state]}
                  </span>
                ) : null}
                {!locked ? (
                  <button
                    type="button"
                    aria-label={`Retirer la photo ${i + 1}`}
                    onClick={() => onChange(files.filter((_, j) => j !== i))}
                    className={`absolute right-0.5 top-0.5 flex size-11 items-center justify-center rounded-full text-lg ${tone.overlay}`}
                  >
                    ×
                  </button>
                ) : null}
                {st?.state === 'error' ? <p className={`mt-1 text-xs ${tone.danger}`}>{st.message}</p> : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/** Aperçu local d'un fichier (URL d'objet libérée au démontage). */
export function Preview({ file, alt, className = 'aspect-square w-full rounded-lg object-cover' }: { file: Blob & { name?: string }; alt: string; className?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    if (typeof URL.createObjectURL !== 'function') return;
    let created: string | null = null;
    queueMicrotask(() => {
      if (!live.current) return;
      try {
        created = URL.createObjectURL(file);
        setUrl(created);
      } catch {
        // aperçu impossible : le nom du fichier reste affiché
      }
    });
    return () => {
      live.current = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [file]);
  return url ? (
    <img src={url} alt={alt} className={className} />
  ) : (
    <div className={`flex aspect-square items-center justify-center overflow-hidden rounded-lg p-1 text-xs ${tone.surfaceMuted} ${tone.textMuted}`}>
      {file.name ?? alt}
    </div>
  );
}
